import React, { useEffect, useState } from 'react';
import { Button, Icon, Select, Title } from '@gerdau/hefesto';
import { Table } from '@gerdau/hefesto-mais-web/Table';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ApplicationState } from '../../../../../../store';
import { editPavement } from '../../../../../../store/ducks/fabrication-mockups/actions';
import {
  EPavementConcretingStatus,
  IPavement
} from '../../../../../../store/ducks/fabrication-mockups/types';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../../utils/Tagging';
import { ActionsContainer, HeaderContainer, TableContainer } from './styles';

export interface IPavementStatusProps {
  pavement: IPavement;
  mockupId: number;
  contractNumber: string;
}

interface IPavementStatusDataSource {
  element: string;
  concretingDate: string;
  pavementStatus: EPavementConcretingStatus;
  key: number;
}

enum EPavementType {
  SLAB = 'Laje',
  PILLAR = 'Pilar',
  BEAM = 'Viga'
}

type PavementsStatus = {
  [key in EPavementType]: PavementsStatusProps;
};

type PavementsStatusProps = {
  status: EPavementConcretingStatus;
  date: string;
  name: EPavementType;
};

const PavementStatus: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { pavement, mockupId, contractNumber } =
    location.state as IPavementStatusProps;

  const editPavementState = useSelector(
    (appState: ApplicationState) =>
      appState.fabricationMockups.editPavementState
  );

  const getPavementsConcretingNotInitialized = (
    slabDate: string,
    slabStatus: EPavementConcretingStatus,
    pillarDate: string,
    pillarStatus: EPavementConcretingStatus,
    beamDate: string,
    beamStatus: EPavementConcretingStatus
  ): PavementsStatus => {
    return {
      [EPavementType.SLAB]: {
        date: slabDate,
        status: slabStatus,
        name: EPavementType.SLAB
      },
      [EPavementType.PILLAR]: {
        date: pillarDate,
        status: pillarStatus,
        name: EPavementType.PILLAR
      },
      [EPavementType.BEAM]: {
        date: beamDate,
        status: beamStatus,
        name: EPavementType.BEAM
      }
    };
  };

  const [
    pavementsConcretingNotInitialized,
    setPavementsConcretingNotInitialized
  ] = useState<PavementsStatus>(
    getPavementsConcretingNotInitialized(
      pavement.slabDate || '',
      pavement.slabStatus || EPavementConcretingStatus.NOT_INITIALIZED,
      pavement.pillarDate || '',
      pavement.pillarStatus || EPavementConcretingStatus.NOT_INITIALIZED,
      pavement.beamDate || '',
      pavement.beamStatus || EPavementConcretingStatus.NOT_INITIALIZED
    )
  );

  const [pavementsConcreting, setPavementsConcreting] =
    useState<PavementsStatus>(pavementsConcretingNotInitialized);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const hasElementChanged = (pavStatus: PavementsStatusProps): boolean => {
    const { date, name, status } = pavStatus;
    const oldStatus = pavementsConcretingNotInitialized[name];
    return date !== oldStatus.date || status !== oldStatus.status;
  };

  useEffect(() => {
    const isDisabled = !Object.values(pavementsConcreting).some((pavStatus) =>
      hasElementChanged(pavStatus)
    );
    setIsDisabled(isDisabled);
  }, [pavementsConcreting]);

  const updatePavementsConcretingDate = (
    pavementType: EPavementType,
    date: string
  ) => {
    const oldPavementConcreting = pavementsConcreting[pavementType];

    setPavementsConcreting({
      ...pavementsConcreting,
      [pavementType]: { ...oldPavementConcreting, date }
    });
  };

  const updatePavementsConcretingStatus = (
    pavementType: EPavementType,
    status: EPavementConcretingStatus
  ) => {
    const oldPavementConcreting = pavementsConcreting[pavementType];

    setPavementsConcreting({
      ...pavementsConcreting,
      [pavementType]: { ...oldPavementConcreting, status }
    });
  };

  const pavementStatusList: TSelectOptionProps[] = [
    {
      label: EPavementConcretingStatus.WAITING_MATERIAL,
      value: EPavementConcretingStatus.WAITING_MATERIAL
    },
    {
      label: EPavementConcretingStatus.MATERIAL_AVAILABLE,
      value: EPavementConcretingStatus.MATERIAL_AVAILABLE
    },
    {
      label: EPavementConcretingStatus.DONE,
      value: EPavementConcretingStatus.DONE
    },
    {
      label: EPavementConcretingStatus.NOT_INITIALIZED,
      value: EPavementConcretingStatus.NOT_INITIALIZED
    }
  ];

  const dataSource: IPavementStatusDataSource[] = Object.values(
    pavementsConcreting
  ).map((pavement, idx) => {
    return {
      key: idx,
      element: pavement.name,
      concretingDate: pavement.date,
      pavementStatus: pavement.status
    };
  });

  const getColumns = () => {
    return [
      {
        dataIndex: 'element',
        key: 'element',
        render: (element: string) => <div>{element}</div>,
        title: 'Elemento',
        sorter: (a: IPavementStatusDataSource, b: IPavementStatusDataSource) =>
          a.element.localeCompare(b.element)
      },
      {
        dataIndex: 'concretingDate',
        key: 'concretingDate',
        title: 'Data de concretagem',
        render: (
          concretingDate: string,
          pavementStatusData: IPavementStatusDataSource
        ) => {
          return (
            <DateField
              label={''}
              name={'concretingDate'}
              selected={
                concretingDate
                  ? moment(concretingDate, 'YYYY-MM-DD').toDate()
                  : undefined
              }
              onChange={(newDate: DateType) => {
                if (newDate) {
                  updatePavementsConcretingDate(
                    pavementStatusData.element as EPavementType,
                    moment(newDate).format('YYYY-MM-DD').toString()
                  );
                }
              }}
              minDate={moment().add(1, 'day').toDate()}
              disabled={editPavementState?.loading}
            />
          );
        },
        sorter: (a: IPavementStatusDataSource, b: IPavementStatusDataSource) =>
          moment(a.concretingDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.concretingDate, 'YYYY-MM-DD').toDate().getTime()
      },
      {
        dataIndex: 'pavementStatus',
        key: 'pavementStatus',
        title: 'Status',
        render: (
          pavementStatus: EPavementConcretingStatus,
          pavementStatusData: IPavementStatusDataSource
        ) => {
          return (
            <Select
              label={''}
              name={'pavementStatus'}
              options={pavementStatusList}
              value={pavementStatus.toString()}
              onSelect={(value: string | string[]) =>
                updatePavementsConcretingStatus(
                  pavementStatusData.element as EPavementType,
                  value as EPavementConcretingStatus
                )
              }
              disabled={editPavementState?.loading}
              placeholder={'- Selecione - '}
            />
          );
        },

        sorter: (a: IPavementStatusDataSource, b: IPavementStatusDataSource) =>
          a.pavementStatus.localeCompare(b.pavementStatus)
      }
    ];
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    location.pathname,
    true
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        event: 'page_view',
        screenName,
        page: location.pathname
      })
    );
  }, []);

  const handleUpdatePavementStatus = () => {
    dispatch(
      editPavement(
        contractNumber,
        {
          ...pavement,
          slabStatus: pavementsConcreting[EPavementType.SLAB].status,
          slabDate: pavementsConcreting[EPavementType.SLAB].date,
          beamStatus: pavementsConcreting[EPavementType.BEAM].status,
          beamDate: pavementsConcreting[EPavementType.BEAM].date,
          pillarStatus: pavementsConcreting[EPavementType.PILLAR].status,
          pillarDate: pavementsConcreting[EPavementType.PILLAR].date
        },
        mockupId
      )
    );

    setPavementsConcretingNotInitialized(
      getPavementsConcretingNotInitialized(
        pavementsConcreting[EPavementType.SLAB].date,
        pavementsConcreting[EPavementType.SLAB].status,
        pavementsConcreting[EPavementType.PILLAR].date,
        pavementsConcreting[EPavementType.PILLAR].status,
        pavementsConcreting[EPavementType.BEAM].date,
        pavementsConcreting[EPavementType.BEAM].status
      )
    );
    setIsDisabled(true);

    dispatch(
      sendTagging({
        event: 'click',
        screenName,
        itemDescription: `${itemDescription}salvar`
      })
    );
  };

  return (
    <>
      <HeaderContainer>
        <Button
          name="goBackButton"
          onClick={() => {
            history.goBack();
          }}
          icon={<Icon option="ArrowLeft" />}
          variant="tertiary"
          disabled={editPavementState?.loading}
          loading={editPavementState?.loading}
        >
          Voltar
        </Button>
        <Title level={1}>{`Atualizar ${pavement.name}`}</Title>
        <span>Insira a data prevista para concretagem</span>
      </HeaderContainer>

      <TableContainer>
        <Table columns={getColumns()} dataSource={dataSource}></Table>
      </TableContainer>

      <ActionsContainer>
        <Button
          onClick={() => {
            dispatch(
              sendTagging({
                event: 'click',
                screenName,
                itemDescription: `${itemDescription}cancelar`
              })
            );
            history.goBack();
          }}
          name={'cancel'}
          variant={'secondary'}
          disabled={editPavementState?.loading}
          loading={editPavementState?.loading}
        >
          Cancelar
        </Button>
        <Button
          name={'save'}
          loading={editPavementState?.loading}
          disabled={editPavementState?.loading || isDisabled}
          onClick={handleUpdatePavementStatus}
        >
          Salvar
        </Button>
      </ActionsContainer>
    </>
  );
};

export default PavementStatus;
